import { FixedMarker } from 'baseui/map-marker';
import { memo, useEffect } from 'react';
import { Marker, MarkerDragEvent, useMap } from 'react-map-gl/mapbox';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  JOURNEY_END_POINT,
  JOURNEY_START_POINT,
  JOURNEY_STATUS_ACTIVE,
  JOURNEY_STATUS_FINISHED,
} from '../../../constants/journeys';
import { useCamera } from '../../../hooks/useCamera';
import { editingJourneyState, journeySelectPointState, selectedJourneyState } from '../../../storage/journeys';
import { JourneySelectPoint } from '../../../types/journey';
import { getAddressFromPoint } from '../../../utils/geocode';
import { getJourneyStatus } from '../../../utils/journeys';

function JourneyMarkers() {
  const { map } = useMap();
  const { zoomTo, fitToLngLats } = useCamera();
  const [editingJourney, setEditingJourney] = useRecoilState(editingJourneyState);
  const journeyStatus = useRecoilValue(selectedJourneyState)?.status;
  const journeySelectPoint = useRecoilValue(journeySelectPointState);

  useEffect(() => {
    if (!map) {
      return;
    }
    if (journeySelectPoint) {
      map.getCanvas().style.cursor = 'crosshair';
    } else {
      map.getCanvas().style.cursor = '';
    }
    return () => {
      map.getCanvas().style.cursor = '';
    };
  }, [map, journeySelectPoint]);

  useEffect(() => {
    if (editingJourney?.startPoint && editingJourney.endPoint) {
      fitToLngLats([
        [editingJourney.startPoint.lng, editingJourney.startPoint.lat],
        [editingJourney.endPoint.lng, editingJourney.endPoint.lat],
      ]);
    } else if (editingJourney?.startPoint) {
      zoomTo(editingJourney.startPoint);
    } else if (editingJourney?.endPoint) {
      zoomTo(editingJourney.endPoint);
    }
  }, [editingJourney?.startPoint, editingJourney?.endPoint, zoomTo, fitToLngLats]);

  if (!editingJourney) return null;

  const status = journeyStatus ? getJourneyStatus(journeyStatus) : undefined;
  const isActive = status?.type === JOURNEY_STATUS_ACTIVE;
  const isFinished = status?.type === JOURNEY_STATUS_FINISHED;

  const onDragEnd = async (event: MarkerDragEvent, pointType: JourneySelectPoint) => {
    const address = await getAddressFromPoint(event.lngLat);
    if (pointType === JOURNEY_START_POINT) {
      setEditingJourney((prev) => ({
        ...prev!,
        startPoint: { ...event.lngLat },
        originAddress: address,
      }));
    } else if (pointType === JOURNEY_END_POINT) {
      setEditingJourney((prev) => ({
        ...prev!,
        endPoint: { ...event.lngLat },
        destinationAddress: address,
      }));
    }
  };

  return (
    <>
      {editingJourney.startPoint && (
        <Marker
          longitude={editingJourney.startPoint.lng}
          latitude={editingJourney.startPoint.lat}
          anchor="bottom"
          draggable={!isActive && !isFinished}
          onDragEnd={(e) => onDragEnd(e, JOURNEY_START_POINT)}
        >
          <FixedMarker
            label="Partida"
            size="small"
          />
        </Marker>
      )}
      {editingJourney.endPoint && (
        <Marker
          longitude={editingJourney.endPoint.lng}
          latitude={editingJourney.endPoint.lat}
          anchor="bottom"
          draggable={!isFinished}
          onDragEnd={(e) => onDragEnd(e, JOURNEY_END_POINT)}
        >
          <FixedMarker
            label="Llegada"
            size="small"
          />
        </Marker>
      )}
    </>
  );
}

export default memo(JourneyMarkers);
